import React, { useState } from "react";
import axios from "axios";
import { Mail, MessageCircle, HelpCircle, ChevronDown, ChevronRight, Send, BookOpen, Shield, Trophy, Clapperboard, UserPlus } from "lucide-react";
import { howToGuides, roleGuides } from "./supportGuides";
import { useDynamicPricing } from "@/lib/subscriptionPricing";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const contactMethods = [
  { icon: Mail, title: "Email Us", detail: "info@ysvchi.com", sub: "We respond within 24 hours", color: "cyan" },
  { icon: MessageCircle, title: "In-App Chat", detail: "Available to Pro users", sub: "Get help right in the app", color: "orange" },
  { icon: HelpCircle, title: "FAQ", detail: "Quick answers below", sub: "Most questions answered", color: "green" },
];

// Priced from live Stripe data (`p`) rather than hardcoded, so a Dashboard price
// change doesn't leave the support FAQ quoting a number the checkout contradicts.
const buildFaqCategories = (p) => [
  {
    title: "Getting Started",
    items: [
      { q: "How do I create an account?", a: "Download the YSV app from the App Store or Google Play, or visit our website and click 'Get Started'. Follow the prompts to create your account with your email address." },
      { q: "What roles are available?", a: "YSV supports several roles including Players, Parents, Coaches, Organization Admins, League Admins, and Gameday Operators. Each role has specific permissions and access levels." },
      { q: "How do I join a team?", a: "Your coach or organization admin can send you an invitation via email. Click the link in the invitation to join your team automatically." },
    ]
  },
  {
    title: "Videos & Clips",
    items: [
      { q: "How do I watch a game?", a: "Navigate to your team's page and select the game you want to watch. Full game videos are available once uploaded by the Gameday Operator." },
      { q: "How do I create a clip?", a: "While watching a game, use the clip creation tool to mark the start and end points of the moment you want to save. Give it a title and save it to your library." },
      { q: "How do I create a highlight tape?", a: "Go to your clips library, select the clips you want to include, and use the 'Create Highlight Tape' feature to combine them into a professional reel." },
      { q: "Can I share clips on social media?", a: "Yes! Every clip and highlight tape has a share button that lets you post directly to Instagram, Twitter, TikTok, and other platforms." },
    ]
  },
  {
    title: "Subscription & Billing",
    items: [
      // Held until the plans call settles: before that `p` is the youth default,
      // so a logged-in amateur could briefly see the wrong product/tier.
      { q: "How much does YSV cost?", a: p.ready
          ? `${p.product_name} is ${p.monthly_label} for players and families. League/Organization signup is free. We also offer custom Gameday Partner plans for larger organizations.`
          : "Member subscriptions are billed monthly or annually — see the Pricing page for current rates. League/Organization signup is free. We also offer custom Gameday Partner plans for larger organizations." },
      { q: "Is there a free trial?", a: "Yes! All new users get a 7-day free trial with full access to all features. No credit card required." },
      { q: "Can I cancel my subscription?", a: "Absolutely. You can cancel anytime from your account settings. You'll retain access until the end of your current billing period." },
    ]
  },
  {
    title: "Technical Issues",
    items: [
      { q: "The video won't play. What should I do?", a: "First, check your internet connection. Try closing and reopening the app. If the issue persists, try clearing the app cache or reinstalling. Contact support if the problem continues." },
      { q: "I forgot my password. How do I reset it?", a: "Click 'Forgot Password' on the sign-in screen. Enter your email address and we'll send you a reset link." },
      { q: "The app is crashing. What should I do?", a: "Make sure you're running the latest version of the app. Try restarting your device. If the issue persists, contact our support team with details about your device and what you were doing when the crash occurred." },
    ]
  },
];

const FAQItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="faq-item" style={{ marginBottom: 8 }}>
      <button className="faq-trigger" onClick={() => setOpen(!open)}>
        {q}
        <ChevronDown size={16} style={{
          color: 'var(--text-secondary)',
          transition: 'transform 0.2s',
          transform: open ? 'rotate(180deg)' : 'rotate(0)',
          flexShrink: 0,
        }} />
      </button>
      {open && <div className="faq-content animate-fade-in">{a}</div>}
    </div>
  );
};

/* ═══ Expandable How-To card with numbered steps + tips ═══ */
const HowToCard = ({ guide, Icon }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`howto-${guide.id}`} className="glass-card" style={{ padding: 20 }}>
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        textAlign: 'left', color: '#fff',
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: 'rgba(0,229,255,0.12)', border: '1px solid rgba(0,229,255,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} style={{ color: 'var(--cyan)' }} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, fontFamily: "'Outfit', sans-serif" }}>{guide.title}</h3>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{guide.summary}</p>
          <p style={{ fontSize: 10, color: 'var(--cyan)', marginTop: 6, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>For: {guide.audience}</p>
        </div>
        <ChevronRight size={18} style={{
          color: 'var(--text-secondary)', flexShrink: 0,
          transition: 'transform 0.2s',
          transform: open ? 'rotate(90deg)' : 'rotate(0)',
        }} />
      </button>
      {open && (
        <div data-testid={`howto-${guide.id}-content`} style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--dark-border)', animation: 'fadeIn 0.25s ease' }}>
          <ol style={{ paddingLeft: 0, counterReset: 'step', listStyle: 'none' }}>
            {guide.steps.map((s, i) => (
              <li key={i} style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                <span style={{
                  width: 24, height: 24, flexShrink: 0,
                  borderRadius: '50%', background: 'var(--cyan)',
                  color: '#000', fontSize: 12, fontWeight: 800,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: "'DM Sans', sans-serif",
                }}>{i + 1}</span>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{s.h}</p>
                  <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.55 }}>{s.b}</p>
                </div>
              </li>
            ))}
          </ol>
          {guide.tips && guide.tips.length > 0 && (
            <div style={{
              marginTop: 14, padding: '12px 14px',
              background: 'rgba(255,152,0,0.06)', border: '1px solid rgba(255,152,0,0.25)',
              borderRadius: 10,
            }}>
              <p style={{ fontSize: 11, fontWeight: 800, color: '#ff9800', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Pro Tips</p>
              <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.7 }}>
                {guide.tips.map((t, i) => <li key={i}>{t}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ═══ Expandable Role Guide card ═══ */
const RoleGuideCard = ({ guide }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`roleguide-${guide.id}`} className="glass-card" style={{ padding: 20 }}>
      <button onClick={() => setOpen(!open)} style={{
        display: 'flex', alignItems: 'flex-start', gap: 12, width: '100%',
        background: 'none', border: 'none', padding: 0, cursor: 'pointer',
        textAlign: 'left', color: '#fff',
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: 10, flexShrink: 0,
          background: 'rgba(76,175,80,0.12)', border: '1px solid rgba(76,175,80,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Shield size={18} style={{ color: '#4caf50' }} />
        </div>
        <div style={{ flex: 1 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 4, fontFamily: "'Outfit', sans-serif" }}>{guide.title}</h3>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.45 }}>{guide.intro}</p>
          <p style={{ fontSize: 10, color: '#4caf50', marginTop: 6, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Role: {guide.persona}</p>
        </div>
        <ChevronRight size={18} style={{
          color: 'var(--text-secondary)', flexShrink: 0,
          transition: 'transform 0.2s',
          transform: open ? 'rotate(90deg)' : 'rotate(0)',
        }} />
      </button>
      {open && (
        <div data-testid={`roleguide-${guide.id}-content`} style={{ marginTop: 18, paddingTop: 18, borderTop: '1px solid var(--dark-border)' }}>
          {guide.sections.map((sec, i) => (
            <div key={i} style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 13, fontWeight: 700, color: 'var(--cyan)', marginBottom: 8, letterSpacing: '0.03em' }}>{sec.h}</h4>
              <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: 12, lineHeight: 1.7 }}>
                {sec.b.map((line, j) => <li key={j}>{line}</li>)}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SupportPage = () => {
  const pricing = useDynamicPricing();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
    setSending(false);
  };

  return (
    <div data-testid="support-page" className="page-gradient" style={{ paddingTop: 120 }}>
      {/* Hero */}
      <section style={{ textAlign: 'center', padding: '40px 24px 48px', position: 'relative', zIndex: 1 }}>
        <p className="animate-fade-in-up" style={{
          color: 'var(--cyan)', fontSize: 13, fontWeight: 600,
          letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 20,
        }}>SUPPORT</p>
        <h1 className="animate-fade-in-up stagger-1" style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 'clamp(32px, 5vw, 54px)',
          fontWeight: 800, lineHeight: 1.1, marginBottom: 16,
        }}>How Can We Help?</h1>
        <p className="animate-fade-in-up stagger-2" style={{
          color: 'var(--text-secondary)', fontSize: 16,
        }}>Find answers to common questions or reach out to our support team.</p>
      </section>

      {/* Contact Methods */}
      <section style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: '0 24px 64px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 16,
      }}>
        {contactMethods.map((m, i) => (
          <div key={m.title} data-testid={`contact-method-${i}`} className="glass-card animate-fade-in-up" style={{
            padding: 28,
            textAlign: 'center',
            opacity: 0,
            animationDelay: `${i * 0.1}s`,
            animationFillMode: 'forwards',
          }}>
            <div className={`icon-box icon-box-${m.color}`} style={{ margin: '0 auto 16px' }}>
              <m.icon size={22} />
            </div>
            <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 6 }}>{m.title}</h3>
            <p style={{ color: 'var(--cyan)', fontSize: 14, marginBottom: 4 }}>{m.detail}</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{m.sub}</p>
          </div>
        ))}
      </section>

      {/* How-To Guides — quick step-by-step playbooks */}
      <section style={{ maxWidth: 880, margin: '0 auto', padding: '0 24px 48px' }}>
        <div className="glow-line" style={{ marginBottom: 32 }} />
        <h2 data-testid="support-howto-heading" style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 28, fontWeight: 700, textAlign: 'center', marginBottom: 8,
        }}>How-To Guides</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', marginBottom: 28 }}>
          Step-by-step playbooks for the things you'll do every week.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14 }}>
          {howToGuides.map((g, idx) => {
            const Icon = [Clapperboard, Trophy, UserPlus][idx] || BookOpen;
            return <HowToCard key={g.id} guide={g} Icon={Icon} />;
          })}
        </div>
      </section>

      {/* Complete role guides — one per persona */}
      <section style={{ maxWidth: 880, margin: '0 auto', padding: '0 24px 48px' }}>
        <div className="glow-line" style={{ marginBottom: 32 }} />
        <h2 data-testid="support-roleguides-heading" style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 28, fontWeight: 700, textAlign: 'center', marginBottom: 8,
        }}>Complete Role Guides</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', marginBottom: 28 }}>
          End-to-end walkthroughs tailored to your role on YSV.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 14, marginBottom: 28 }}>
          {roleGuides.map(g => <RoleGuideCard key={g.id} guide={g} />)}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ maxWidth: 700, margin: '0 auto', padding: '0 24px 64px' }}>
        <div className="glow-line" style={{ marginBottom: 48 }} />
        <h2 data-testid="support-faq-heading" style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 28, fontWeight: 700, textAlign: 'center', marginBottom: 40,
        }}>Frequently Asked Questions</h2>
        
        {buildFaqCategories(pricing).map(cat => (
          <div key={cat.title} style={{ marginBottom: 32 }}>
            <h3 style={{
              color: 'var(--cyan)', fontSize: 14, fontWeight: 600,
              marginBottom: 12, letterSpacing: '0.03em',
            }}>{cat.title}</h3>
            {cat.items.map(item => <FAQItem key={item.q} q={item.q} a={item.a} />)}
          </div>
        ))}
      </section>

      {/* Contact Form */}
      <section style={{
        maxWidth: 560,
        margin: '0 auto',
        padding: '0 24px 80px',
      }}>
        <div className="glow-line" style={{ marginBottom: 48 }} />
        <h2 style={{
          fontFamily: "'Outfit', sans-serif",
          fontSize: 24, fontWeight: 700, textAlign: 'center', marginBottom: 8,
        }}>Still Need Help?</h2>
        <p style={{
          color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', marginBottom: 32,
        }}>Send us a message and we'll get back to you.</p>

        <form data-testid="contact-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <input
              className="input-field"
              data-testid="contact-name"
              placeholder="Your Name"
              value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              required
            />
            <input
              className="input-field"
              data-testid="contact-email"
              type="email"
              placeholder="Your Email"
              value={form.email}
              onChange={e => setForm({...form, email: e.target.value})}
              required
            />
          </div>
          <input
            className="input-field"
            data-testid="contact-subject"
            placeholder="Subject"
            value={form.subject}
            onChange={e => setForm({...form, subject: e.target.value})}
          />
          <textarea
            className="input-field"
            data-testid="contact-message"
            placeholder="Your message..."
            rows={5}
            value={form.message}
            onChange={e => setForm({...form, message: e.target.value})}
            required
            style={{ resize: 'vertical' }}
          />
          <button
            type="submit"
            data-testid="contact-submit"
            className="btn-primary"
            disabled={sending}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              opacity: sending ? 0.7 : 1,
            }}
          >
            <Send size={16} />
            {sending ? 'Sending...' : 'Send Message'}
          </button>
          {status === 'success' && (
            <p data-testid="contact-success" style={{ color: '#4caf50', fontSize: 14, textAlign: 'center' }}>
              Message sent successfully! We'll get back to you soon.
            </p>
          )}
          {status === 'error' && (
            <p data-testid="contact-error" style={{ color: '#f44336', fontSize: 14, textAlign: 'center' }}>
              Something went wrong. Please try again.
            </p>
          )}
        </form>
      </section>
    </div>
  );
};

export default SupportPage;
